import { useAuth0 } from '@auth0/auth0-react';
import { Link, useLocation } from '@tanstack/react-router';
import { Activity, Zap, TrendingUp, Watch, Route } from 'lucide-react';
import { Button } from './ui/button';
import { useGarminOAuth } from '../hooks/useGarminOAuth';

export const ConnectionPrompt = () => {
  const location = useLocation();
  const { user: auth0User } = useAuth0();
  const { connect: connectGarmin, isStarting: isStartingGarmin } =
    useGarminOAuth();

  return (
    <div className="surface-card overflow-hidden p-6 sm:p-8">
      <div className="text-center">
        {/* Header */}
        <div className="flex justify-center mb-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-midnight shadow-lg">
            <Activity className="h-8 w-8 text-white" />
          </div>
        </div>

        <h3 className="mb-2 mt-5 text-2xl font-bold text-brand-midnight dark:text-white">
          Connect your fitness account
        </h3>

        <p className="mx-auto mb-6 max-w-md text-gray-600 dark:text-gray-300">
          Connect Strava or Garmin to import activities, track your progress,
          and build a race plan from your real training history.
        </p>

        {/* Benefits */}
        <div className="mx-auto mb-7 grid max-w-sm grid-cols-2 gap-3">
          <div className="flex flex-col items-center rounded-xl border border-gray-100 bg-white/70 p-3 shadow-sm dark:border-white/10 dark:bg-white/10">
            <TrendingUp className="h-5 w-5 text-brand-cobalt mb-1" />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
              Track Progress
            </span>
          </div>
          <div className="flex flex-col items-center rounded-xl border border-gray-100 bg-white/70 p-3 shadow-sm dark:border-white/10 dark:bg-white/10">
            <Route className="mb-1 h-5 w-5 text-brand-green" />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
              Race plans
            </span>
          </div>
          <div className="flex flex-col items-center rounded-xl border border-gray-100 bg-white/70 p-3 shadow-sm dark:border-white/10 dark:bg-white/10">
            <Zap className="h-5 w-5 text-purple-600 mb-1" />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
              Auto sync
            </span>
          </div>
          <div className="flex flex-col items-center rounded-xl border border-gray-100 bg-white/70 p-3 shadow-sm dark:border-white/10 dark:bg-white/10">
            <Activity className="h-5 w-5 text-orange-600 mb-1" />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
              Training history
            </span>
          </div>
        </div>

        {/* Connect Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to={`${import.meta.env.REACT_APP_API_SERVICE_URI}/auth/strava?email=${encodeURIComponent(auth0User?.email || '')}&from=${encodeURIComponent(
              `${window.location.origin}${location.pathname}`,
            )}`}
          >
            <Button className="w-full rounded-full bg-[#FC4C02] px-6 py-3 font-semibold text-white transition-all duration-200 hover:bg-[#e64600] sm:w-auto">
              <Activity className="h-5 w-5 mr-2" />
              Connect with Strava
            </Button>
          </Link>

          <Button
            disabled={isStartingGarmin}
            onClick={() => void connectGarmin(location.pathname)}
            className="w-full rounded-full bg-[#007CC3] px-6 py-3 font-semibold text-white transition-all duration-200 hover:bg-[#005a8a] sm:w-auto"
          >
            <Watch className="h-5 w-5 mr-2" />
            {isStartingGarmin ? 'Connecting…' : 'Connect with Garmin'}
          </Button>
        </div>

        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
          We only access your workout data to provide personalized insights
        </p>
      </div>
    </div>
  );
};
